<script setup lang="ts">
/**
 * @author Alexander Kenchoshvili
 */

import { computed } from "vue";
import type { Flat } from "@/store/building/types/StateType";

import Button from "@/components/regularComponents/UIComponents/Button.vue";
import { useMainStore } from "@/store/main";
const Picture = defineAsyncComponent(
  () => import("@/components/regularComponents/UIComponents/Picture.vue")
);

const props = defineProps<{
  flatData: Flat;
  filteredData: Flat[];
}>();

const route = computed(() => {
  const flatDataRouteToArr: string[] = props.flatData.route?.split("/");
  flatDataRouteToArr[3] = `floor-${props.flatData.floor_number}`;

  const url = "/" + flatDataRouteToArr?.join("/");
  return url || "#";
});
const flatType = computed(() => {
  const id = props.flatData.taxonomy?.flat_type?.[0];
  const match = useMainStore()?.terms?.flat_type?.find(
    (item) => item?.id === Number(id)
  );

  return match?.title;
});
</script>

<template>
  <router-link class="special-offer__card-wrapper d-flex j-center" :to="route">
    <div class="special-offers__card d-flex dir-col j-start">
      <div
        class="special-offers__card--image-wrapper pos-rel w-full d-flex j-center al-center"
      >
        <Picture
          class="special-offers__card--image w-full"
          :devices="flatData?.flat_type?.image3d?.[0]?.devices"
          imgClass="img-absolute"
          :alt="flatData?.flat_type?.title"
        />
      </div>
      <div class="special-offers__card-footer">
        <h2 class="special-offers__card--title hl-3-bold">
          {{ flatType }} - {{ flatData?.flat_number }} {{ $t("room") }}
        </h2>
        <div>
          <span
            v-if="filteredData.some((item) => item?.flat_type?.teaser)"
            class="special-offers__card--teaser P-12"
            >{{ flatData?.flat_type?.teaser }}</span
          >
          <div class="special-offers__card--price d-flex al-center j-btw">
            <div class="special-offers__card--price-info">
              <span class="price-title p-16">{{ $t("price from") }}</span>
              <div class="price-box d-flex al-end">
                <p class="new-price hl-2-light">
                  {{ flatData?.offer_price }} <sup>$</sup>
                </p>
                <span class="price-dash">/</span>
                <div class="d-flex old-price-wrapper">
                  <p class="old-price hl-2-light">{{ flatData?.price }}</p>
                  <sup>$</sup>
                </div>
              </div>
            </div>
            <Button class="price-button" btn-text="show offer" />
          </div>
        </div>
      </div>
    </div>
  </router-link>
</template>

<style lang="scss">
.special-offers__card {
  width: 100%;
  &--image-wrapper {
    background-color: $lotion;
    margin-bottom: 61px;
    &::before {
      content: "";
      display: block;
      padding-top: 116.45569620253164%;
    }
    @include maxq(desktop-2k) {
      margin-bottom: 39px;
    }
    @include maxq(desktop-lg) {
      margin-bottom: 24px;
    }
  }
  &--image {
    height: auto;
    .img-absolute {
      object-fit: contain;
    }
  }
  &-footer {
    background-color: $white;
    flex: 1 0 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
  .button {
    height: unset;
    color: $black;
    padding: 0;
    white-space: nowrap;
    @include weight(500);
  }
  .button__content {
    text-transform: capitalize;
    font-size: 32px;
    line-height: 32px;
    padding: 40px 48px;
    @include maxq(desktop-2k) {
      font-size: 21px;
      line-height: 24px;
      padding: 26px 34px;
    }
    @include maxq(desktop-lg) {
      font-size: 15px;
      padding: 16px 24px;
    }
  }

  &--title {
    color: $black;
    font-size: 48px;
    line-height: 40px;
    margin-bottom: 28px;
    @include clamp(1);
    @include maxq(desktop-2k) {
      font-size: 32px;
      margin-bottom: 8px;
    }
    @include maxq(desktop-lg) {
      font-size: 24px;
      line-height: 32px;
    }
  }
  &--teaser {
    // min-height: 156px;

    @include maxq(desktop-2k) {
      // min-height: 102px;
    }

    @include maxq(desktop-lg) {
      // min-height: 78px;
    }

    color: $black;
    font-size: 32px;
    line-height: 52px;
    margin-bottom: 47px;
    @include clamp(3);
    @include maxq(desktop-2k) {
      font-size: 22px;
      line-height: 34px;
      margin-bottom: 32px;
    }
    @include maxq(desktop-lg) {
      font-size: 15px;
      line-height: 26px;
      margin-bottom: 24px;
    }
  }
  &--price {
    @include maxq(mobile-sm) {
      flex-direction: column;
      align-items: unset;
    }
    &-info {
      @include maxq(mobile-sm) {
        margin-bottom: 24px;
      }
    }
    .price-title {
      font-size: 28px;
      line-height: 48px;
      @include maxq(desktop-2k) {
        font-size: 18px;
        line-height: 38px;
      }
      @include maxq(desktop-lg) {
        font-size: 15px;
        line-height: 23px;
      }
    }
    .new-price,
    .price-dash {
      font-size: 48px;
      line-height: 56px;
      white-space: nowrap;
      @include weight(400);
      @include maxq(desktop-2k) {
        font-size: 32px;
        line-height: 38px;
      }
      @include maxq(desktop-lg) {
        font-size: 24px;
        line-height: 28px;
      }
      @media (max-width: 743px) {
        font-size: 16px;
        line-height: 22px;
      }
      sup {
        font-size: 18px;
        line-height: 28px;
        @include weight(400);
        @include maxq(desktop-2k) {
          font-size: 15px;
        }
        @include maxq(desktop-lg) {
          font-size: 14px;
        }
      }
    }
    .price-dash {
      color: $gray;
    }

    .old-price-wrapper {
      sup {
        color: $gray;
        margin-left: 10px;
        @include maxq(desktop) {
          margin-left: 4px;
          font-size: 10px;
        }
      }
    }
    .old-price {
      font-size: 36px;
      line-height: 36px;
      color: $gray;
      text-decoration: line-through;
      @include weight(400);
      @include maxq(desktop-2k) {
        font-size: 24px;
        line-height: 30px;
      }
      @include maxq(desktop-lg) {
        font-size: 16px;
        line-height: 22px;
      }
      @media (max-width: 743px) {
        font-size: 12px;
        line-height: 16px;
      }
    }
  }
}

@include locale {
  .special-offers__card--title {
    line-height: unset;
  }
}
.special-offer__card-wrapper {
  height: 100%;
}
</style>
